import React from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, ScrollText } from "lucide-react";
import { sharedApi } from "@/lib/api";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";

/**
 * Iter 784 — footer legal pages, owner-managed from the admin Policies
 * screen. `GET /api/policies/{slug}` returns {title, effective,
 * last_updated, intro, sections:[{heading, paragraph, bullets[]}]} and
 * 404s for hidden pages.
 */
export default function LegalPolicyPage() {
  const { slug } = useParams();
  const q = useQuery({
    queryKey: ["policy", slug],
    retry: 0,
    queryFn: async () => (await sharedApi.get(`/policies/${slug}`)).data,
  });

  if (q.isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center text-slate-400" data-testid="policy-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="policy-not-found">
        <Seo title="Page not found" />
        <ScrollText className="w-10 h-10 mx-auto text-slate-300" />
        <h1 className="mt-4 font-display text-2xl font-black text-slate-900">That page isn&apos;t available</h1>
        <p className="mt-2 text-sm text-slate-500">It may have been removed or hidden.</p>
        <Link to="/" className="mt-6 inline-flex rounded-full bg-[color:var(--brand-blue)] text-white font-bold px-6 py-2.5 text-sm">
          Back to home
        </Link>
      </div>
    );
  }

  const p = q.data;
  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-10 md:py-14" data-testid="policy-page">
      <Seo title={p.title} description={p.description || `${p.title} — Allsale Classified`} />
      <Breadcrumbs items={[{ label: p.title }]} />
      <h1 className="mt-6 font-display text-3xl md:text-4xl font-black text-slate-900">{p.title}</h1>
      {(p.effective || p.last_updated) && (
        <p className="mt-2 text-xs text-slate-400" data-testid="policy-updated">
          {p.effective ? `Effective ${p.effective}` : ""}
          {p.effective && p.last_updated ? " · " : ""}
          {p.last_updated ? `Last updated ${p.last_updated}` : ""}
        </p>
      )}
      {p.intro && <p className="mt-5 text-slate-600 leading-relaxed">{p.intro}</p>}

      <div className="mt-8 space-y-8">
        {(p.sections || []).map((s, i) => (
          <section key={i} data-testid={`policy-section-${i}`}>
            {s.heading && (
              <h2 className="font-display text-lg md:text-xl font-bold text-slate-900">{s.heading}</h2>
            )}
            {s.paragraph && <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.paragraph}</p>}
            {Array.isArray(s.bullets) && s.bullets.length > 0 && (
              <ul className="mt-3 space-y-2">
                {s.bullets.map((b, j) => (
                  <li key={j} className="flex gap-2 text-sm text-slate-600 leading-relaxed">
                    <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-[color:var(--brand-blue)] shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>

      {p.contact_email && (
        <p className="mt-10 text-sm text-slate-500">
          Questions? Email{" "}
          <a href={`mailto:${p.contact_email}`} className="font-semibold text-[color:var(--brand-blue)]">
            {p.contact_email}
          </a>
        </p>
      )}
    </div>
  );
}
